import React, { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Modal } from 'utils';
import SingleUpdate from './SingleUpdate';
import LikeButton from '../Like/LikeButton';
import { getAvatarBoxTitle } from '../DetailComponents/AvatarBox';
import ShareProject from '../Share/ShareProject';
import { buildDonationUrl } from '../utils/utm';



function ProjectUpdates({ updates, project }) {
  const [showAllUpdatesModal, setShowAllUpdatesModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const visibleUpdates = updates.slice(0, 3);
  const hasMore = updates.length > 3;
  const uploaderName = getAvatarBoxTitle(project);
  const donationUrl = buildDonationUrl({
    projectId: project?.id,
    donationValue: '30,00',
  });

  const handleShowMore = () => {
    setShowAllUpdatesModal(true);
  };

  const handleCloseModal = () => setShowAllUpdatesModal(false);
  const handleOpenShareModal = () => {
    setShowShareModal(true);
    setShowAllUpdatesModal(false);
  }
  const handleCloseShareModal = () => setShowShareModal(false);

  return (
    <TimeLineTabWrapper>
      <div className="content">
        <TitleWrapper>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h1>Updates</h1>
            <TotalCountWrapper>{updates.length}</TotalCountWrapper>
          </div>
          <div>
            <LikeButton noBorder={true} project={project} />
          </div>
        </TitleWrapper>

        <UpdatesList>
          {visibleUpdates.map((time, index) => (
            <SingleUpdate
              title={time.title}
              key={time?.id || `${time?.date || 'time'}-${index}`}
              time={time.date}
              image={time.image}
              content={time.body || 'There Is No Body'}
              images={time.images || []}
            />
          ))}

          {hasMore && (
            <ShowMoreButton onClick={handleShowMore} role="button" tabIndex={0}>
              Alle Updates anzeigen
            </ShowMoreButton>
          )}
        </UpdatesList>
      </div>

      <Modal
        showModal={showAllUpdatesModal}
        handelCloseModal={handleCloseModal}
        title={uploaderName ? `Updates von ${uploaderName}` : 'Updates'}
        width="812px"
      >
        <UpdatesModalBody>
          {updates.map((time, index) => (
            <ModalUpdateCard key={time?.id || `${time?.date || 'time'}-${index}`}>
              <SingleUpdate
                title={time.title}
                time={time.date}
                image={time.image}
                content={time.body || ''}
                images={time.images || []}
              />
            </ModalUpdateCard>
          ))}

          <ModalActions>
            <ModalShareButton type="button" onClick={handleOpenShareModal}>
              {project?.is_ticketable ? 'Event' : 'Projekt'} teilen
            </ModalShareButton>
            <ModalSupportButton to={donationUrl}>Jetzt unterstützen</ModalSupportButton>
          </ModalActions>
        </UpdatesModalBody>
      </Modal>

      <Modal
        showModal={showShareModal}
        handelCloseModal={handleCloseShareModal}
      >
        <ShareProject
          subtitle={project.subtitle}
          title={project.title}
          banner={project.banner}
        />
      </Modal>
    </TimeLineTabWrapper>
  );
}

ProjectUpdates.propTypes = {
  updates: PropTypes.array.isRequired,
};

const UpdatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ShowMoreButton = styled.div`
  /* Paragraph */
  /* Text md/Semibold */
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  cursor: pointer;
  text-decoration-line: underline;

  color: #9dbc60;
  user-select: none;
  
`;

const UpdatesModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  /* make modal body scrollable and leave space for sticky footer */
  max-height: calc(80vh - 120px);
  overflow-y: auto;

  @media (max-width: 768px) {
    /* on mobile don't reserve large footer space (footer will be inline) */
    max-height: calc(80vh - 100px);
    padding-bottom: 24px;
    gap: 20px;
  }
`;

const ModalUpdateCard = styled.div`
  width: 100%;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  position: sticky;
  bottom: 0;
  padding: 16px 0;
  background: #ffffff;
  box-shadow: 0 -8px 24px rgba(10,13,18,0.06);
  z-index: 1200;

  @media (max-width: 768px) {
    /* on mobile the actions should be inline at the end (not sticky) */
    position: static;
    box-shadow: none;
    padding: 8px 0 0 0;
    z-index: auto;
    flex-direction: column;
  }
`;

const ModalBaseButton = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 16px 22px;
  gap: 8px;
  flex: 1;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  @media (max-width: 768px) {
    width: 100%;
    min-height: 52px;
    font-size: 16px;
    line-height: 24px;
  }
`;

const ModalShareButton = styled(ModalBaseButton)`
  background: #ffffff;
  border: 1px solid #d5d7da;
  color: #414651;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05), inset 0 0 0 1px rgba(10, 13, 18, 0.18), inset 0 -2px 0 rgba(10, 13, 18, 0.05);

  &:hover {
    background: #f8f9fb;
    border-color: #c7c9d0;
  }
`;

const ModalSupportButton = styled(Link)`
  background: #9dbc60;
  border: 1px solid #9dbc60;
  color: #ffffff !important;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05),
    inset 0 0 0 1px rgba(10, 13, 18, 0.18),
    inset 0 -2px 0 rgba(10, 13, 18, 0.05);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 16px 22px;
  gap: 8px;
  flex: 1;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  text-decoration: none;

  a {
    color: #ffffff;
  }
  &:hover {
    background: #8caf54;
    border-color: #8caf54;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 52px;
    font-size: 16px;
    line-height: 24px;
  }
`;

const TotalCountWrapper = styled.div`
  font-size: 14px;

  margin-left: 12px;
  border: 1px solid #ceeab0;
  border-radius: 999px;
  padding: 4px 12px;
  color: #3f621a;
  font-weight: 500;
  line-height: 20px;
  background: #f5fbee;
`;

const TitleWrapper = styled.div`
  h1 {
    font-size: 24px;
    font-weight: bold;
    color: #181d27;
    line-height: 32px;
    margin: 0;
  }

  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TimeLineTabWrapper = styled.div`
  width: 100%;
  margin-top: 30px;
  margin-bottom: 32px;
  @media (max-width: 768px) {
    padding: 10px;
  }
  
  .content {
    margin: 0 auto;
    max-width: 764px;
  }

  .show-more {
    /* Paragraph */
    cursor: pointer;
    font-family: Inter sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 28px;
    /* identical to box height, or 156% */
    text-decoration-line: underline;

    color: #9dbc60;

    /* Inside auto layout */
    flex: none;
    order: 2;
    flex-grow: 0;
  }
`;

export default ProjectUpdates;
